import { z } from "zod";
import { passwordValidation } from "@sunfish/lib";
import { readFileAnswer } from "@sunfish/lib/file-questions";
import { readSignatureAnswer } from "@sunfish/lib/signature-questions";
import { DateTime } from "luxon";
import pkg from "google-libphonenumber";
const { PhoneNumberUtil } = pkg;

const phoneUtil = PhoneNumberUtil.getInstance();

export type FormFieldForValidation = {
  formFieldId: string;
  fieldTypeKey: string | null;
  isRequired: boolean | null;
  formFieldValidationRules?: {
    validationType: string;
    validationValue: string | null;
  }[];
  formFieldOptions?: { formFieldOptionId: string; label: string }[];
  childrenFields?: unknown[];
};

const validatePhoneNumber = (
  value: string,
  regionCode: string | undefined,
  ctx: z.RefinementCtx,
  isRequired: boolean,
): void => {
  if (!value || value.trim().length === 0) {
    if (isRequired) {
      ctx.addIssue({
        code: 'custom',
        message: "Phone number is required",
      });
    }
    return;
  }

  try {
    const phoneNumber = phoneUtil.parse(value, regionCode);

    if (!phoneUtil.isValidNumber(phoneNumber)) {
      const regionName = regionCode === "US" ? "US " : "";
      const isPossible = phoneUtil.isPossibleNumber(phoneNumber);

      if (!isPossible) {
        const reason = phoneUtil.isPossibleNumberWithReason(phoneNumber);
        switch (reason) {
          case PhoneNumberUtil.ValidationResult.TOO_SHORT:
            ctx.addIssue({
              code: 'custom',
              message: `Phone number is too short for a ${regionName}number`,
            });
            return;
          case PhoneNumberUtil.ValidationResult.TOO_LONG:
            ctx.addIssue({
              code: 'custom',
              message: `Phone number is too long for a ${regionName}number`,
            });
            return;
          case PhoneNumberUtil.ValidationResult.INVALID_LENGTH:
            ctx.addIssue({
              code: 'custom',
              message: `Phone number has an invalid length for a ${regionName}number`,
            });
            return;
          default:
            break;
        }
      }

      if (regionCode) {
        const numberRegion = phoneUtil.getRegionCodeForNumber(phoneNumber);
        if (numberRegion && numberRegion !== regionCode) {
          ctx.addIssue({
            code: 'custom',
            message: `This appears to be a ${numberRegion} number, not a ${regionCode} number`,
          });
          return;
        }
      }

      ctx.addIssue({
        code: 'custom',
        message: regionCode
          ? `Please enter a valid ${regionCode} phone number`
          : "Please enter a valid phone number",
      });
    }
  } catch (err) {
    const error = err as Error;
    const message = error.message?.toLowerCase() || "";
    if (message.includes("Invalid country calling code")) {
      ctx.addIssue({
        code: 'custom',
        message: "Please include a valid country code (e.g., +1 for US)",
      });
    } else if (message.includes("not a number")) {
      ctx.addIssue({
        code: 'custom',
        message: "Please enter a valid phone number with digits only",
      });
    } else if (message.includes("country")) {
      ctx.addIssue({
        code: 'custom',
        message: "Please include a valid country code (e.g., +1 for US)",
      });
    } else if (message.includes("too short")) {
      ctx.addIssue({
        code: 'custom',
        message: "Phone number is too short",
      });
    } else if (message.includes("invalid")) {
      ctx.addIssue({
        code: 'custom',
        message: "Please enter a valid phone number",
      });
    } else {
      ctx.addIssue({
        code: 'custom',
        message: (err as Error)?.message || "Invalid phone number",
      });
    }
  }
};

const getValidationRulesObject = (
  validationRules?: {
    validationType: string;
    validationValue: string | null;
  }[],
) => {
  if (!validationRules?.length) return {};
  return validationRules.reduce(
    (acc, rule) => {
      acc[rule.validationType] = rule.validationValue;
      return acc;
    },
    {} as Record<string, string | null>,
  );
};

export const getZodSchemaForField = (
  field: FormFieldForValidation,
): z.ZodTypeAny => {
  let schema: z.ZodTypeAny;
  const validationRulesArray = field.formFieldValidationRules || [];
  const validationRules = getValidationRulesObject(validationRulesArray);
  const requiredMsg = { error: "This field is required" };

  switch (field.fieldTypeKey) {
    case "text":
    case "textarea":
      schema = z
        .string(requiredMsg)
        .min(field.isRequired ? 1 : 0, "This field is required");
      break;

    case "email":
      schema = field.isRequired
        ? z.string(requiredMsg).email("Invalid email address")
        : z.string().refine((val) => {
            if (!val || val.trim().length === 0) return true;
            return z.string().email().safeParse(val).success;
          }, "Invalid email address");
      break;

    case "url":
      schema = z
        .preprocess(
          (val) => {
            if (typeof val !== "string") return val;

            const trimmed = val.trim();
            if (!trimmed) return undefined;

            return /^https?:\/\//i.test(trimmed)
              ? trimmed
              : `http://${trimmed}`;
          },
          field.isRequired
            ? z.string(requiredMsg).url("Please enter a valid URL (e.g. google.com)")
            : z
                .string()
                .url("Please enter a valid URL (e.g. google.com)")
                .optional(),
        )
        .refine((val) => {
          try {
            const { hostname } = new URL(val ?? "");

            return (
              hostname.includes(".") &&
              hostname.split(".").every((x) => {
                const parsedNumber = Number(x);
                return isNaN(parsedNumber);
              })
            );
          } catch {
            return false;
          }
        }, "Please enter a valid domain (e.g. google.com)");
      break;

    case "tel":
      if (validationRules?.phoneFormat === "us") {
        schema = z.coerce.string().superRefine((val, ctx) => {
          validatePhoneNumber(val, "US", ctx, !!field.isRequired);
        });
      } else if (validationRules?.phoneFormat === "international") {
        schema = z.coerce.string().superRefine((val, ctx) => {
          validatePhoneNumber(val, undefined, ctx, !!field.isRequired);
        });
      } else {
        schema = z.string(requiredMsg);
      }
      break;

    case "number": {
      let numberSchema = z.number({ message: "Must be a number" });

      const minValue = validationRules?.min
        ? Number(validationRules.min)
        : null;
      const maxValue = validationRules?.max
        ? Number(validationRules.max)
        : null;

      if (minValue !== null && !isNaN(minValue)) {
        const errorMessage =
          validationRules.errorMessage
            ?.replace("{min}", String(minValue))
            ?.replace("{max}", maxValue ? String(maxValue) : "") ||
          `Must be at least ${minValue}`;
        numberSchema = numberSchema.min(minValue, errorMessage);
      }

      if (maxValue !== null && !isNaN(maxValue)) {
        const errorMessage =
          validationRules.errorMessage
            ?.replace("{min}", minValue ? String(minValue) : "")
            ?.replace("{max}", String(maxValue)) ||
          `Must be at most ${maxValue}`;
        numberSchema = numberSchema.max(maxValue, errorMessage);
      }

      schema = numberSchema;
      break;
    }

    case "boolean":
      schema = z.boolean();
      break;

    case "date":
      schema = z.string(requiredMsg).refine(
        (val) => {
          if (!!val?.length) {
            return DateTime.fromISO(val).isValid;
          }
          return !field.isRequired;
        },
        {
          message: "Invalid date format",
        },
      );
      break;

    case "time":
      schema = z.string(requiredMsg).refine(
        (val) => {
          if (!!val?.length) {
            const [hour, minute] = val.split(":");
            return DateTime.fromObject({
              hour: Number(hour),
              minute: Number(minute),
            }).isValid;
          }
          return !field.isRequired;
        },
        {
          message: "Invalid time format",
        },
      );
      break;

    case "address":
      schema = z.preprocess(
        (val) => {
          if (!val || (typeof val === "string" && val.trim() === "")) return undefined;
          try {
            return typeof val === "string" ? JSON.parse(val) : val;
          } catch {
            return val;
          }
        },
        z.object({
          street1: z.string({ error: "Street address is required" }).min(1, "Street address is required"),
          street2: z.string().optional().nullable(),
          city: z.string({ error: "City is required" }).min(1, "City is required"),
          state: z.string({ error: "State is required" }).min(1, "State is required"),
          zip: z.string({ error: "Zip code is required" }).min(5, "Zip code is required"),
        }),
      );
      break;

    case "address-international":
      schema = z.preprocess(
        (val) => {
          if (!val || (typeof val === "string" && val.trim() === "")) return undefined;
          try {
            return typeof val === "string" ? JSON.parse(val) : val;
          } catch {
            return val;
          }
        },
        z.object({
          street1: z.string({ error: "Street address is required" }).min(1, "Street address is required"),
          street2: z.string().optional().nullable(),
          city: z.string({ error: "City is required" }).min(1, "City is required"),
          province: z.string().optional(),
          postal: z.string({ error: "Postal/Zip code is required" }).min(1, "Postal/Zip code is required"),
          country: z.string({ error: "Country is required" }).min(1, "Country is required"),
        }),
      );
      break;

    case "select":
      schema = field.isRequired
        ? z.string(requiredMsg).refine((val) => z.guid().safeParse(val).success, "Please select an option")
        : z.string().refine((val) => {
            if (!val || val.trim().length === 0) return true;
            return z.guid().safeParse(val).success;
          }, "Please select an option");
      break;

    case "checkbox": {
      // An answer is stored as an option id on some paths and as that option's
      // label on others, so both count as offered. A field whose options were
      // not loaded (an org question keeps them on its parent) goes unchecked
      // rather than refusing everything.
      const offered = new Set(
        (field.formFieldOptions ?? []).flatMap((option) => [option.formFieldOptionId, option.label]),
      );
      schema = z.preprocess(
        (val) => {
          if (typeof val === "string") return [val];
          if (Array.isArray(val)) return val;
          return [];
        },
        z
          .array(z.string())
          .min(field.isRequired ? 1 : 0, "You must select at least one option")
          .refine(
            (values) => offered.size === 0 || values.every((value) => offered.has(value)),
            "Please select an option",
          ),
      );
      break;
    }

    case "radio":
      schema = z
        .string(requiredMsg)
        .min(field.isRequired ? 1 : 0, "You must select an option");
      break;

    case "password":
      schema = passwordValidation;
      break;

    case "file":
      schema = z.unknown().superRefine((val, ctx) => {
        if (val === null || val === undefined || val === "") {
          if (field.isRequired) {
            ctx.addIssue({ code: "custom", message: "Please upload a file" });
          }
          return;
        }
        if (!readFileAnswer(val)) {
          ctx.addIssue({ code: "custom", message: "Please upload the file again" });
        }
      });
      break;

    case "signature":
      schema = z.unknown().superRefine((val, ctx) => {
        if (val === null || val === undefined || val === "") {
          if (field.isRequired) {
            ctx.addIssue({ code: "custom", message: "Type your full name to sign" });
          }
          return;
        }
        const signature = readSignatureAnswer(val);
        if (!signature) {
          ctx.addIssue({ code: "custom", message: "Type your full name to sign" });
          return;
        }
        if (!signature.signedAt && signature.agreed !== true) {
          ctx.addIssue({ code: "custom", message: "Confirm that this is your signature" });
        }
      });
      break;

    default:
      schema = z.string(requiredMsg);
  }

  return field.isRequired ? schema : schema.nullish();
};

export const buildFormFieldsSchema = (
  fields: FormFieldForValidation[],
): z.ZodObject<Record<string, z.ZodTypeAny>> => {
  const schemaShape: Record<string, z.ZodTypeAny> = {};

  for (const field of fields) {
    schemaShape[field.formFieldId] = getZodSchemaForField(field);
  }

  return z.object(schemaShape);
};
